import { Link } from "@tanstack/react-router";
import logo from "@/assets/p2pro-logo.webp";
import { Mail, Phone, MapPin, Instagram, Youtube, Facebook, ArrowRight } from "lucide-react";

const FOOTER_LINKS = {
  plataforma: [
    { label: "Painel IPTV", href: "/painel-iptv" },
    { label: "Revenda IPTV", href: "/revenda-iptv" },
    { label: "Aplicativos", href: "/aplicativos-iptv" },
    { label: "Tutoriais", href: "/tutoriais-iptv" },
    { label: "Curso IPTV", href: "/curso-iptv" },
  ],
  empresa: [
    { label: "Quem Somos", href: "/quem-somos" },
    { label: "Segurança", href: "/transparencia-e-seguranca" },
    { label: "Contato", href: "/contato" },
    { label: "Sitemap", href: "/sitemap" },
  ],
  ajuda: [
    { label: "Privacidade", href: "/politica-de-privacidade" },
    { label: "Termos de Uso", href: "/termos-de-uso" },
    { label: "Cookies", href: "/politica-de-cookies" },
    { label: "LGPD", href: "/lgpd" },
  ]
};

export function Footer() {
  const whatsappUrl = `https://api.whatsapp.com/send?phone=5583986147064&text=${encodeURIComponent("Olá! Vim do site p2propaineliptv.com.br, gostaria de mais informações.")}`;

  return (
    <footer className="border-t border-white/5 py-16 bg-[#0a0c10] relative overflow-hidden">
      {/* Background Ornaments */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-electric/5 blur-[120px] rounded-full -z-10" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-electric/5 blur-[100px] rounded-full -z-10" />
      
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-4 mb-6 group">
              <img src={logo} alt="P2 PRO" className="h-10 w-10 object-contain transition-transform group-hover:scale-110" loading="lazy" width={40} height={40} />
              <span className="font-black text-2xl tracking-tighter uppercase">
                P2<span className="text-electric ml-0.5">PRO</span>
              </span>
            </Link>
            <p className="text-muted-foreground max-w-sm leading-relaxed text-sm mb-8 italic">
              A maior infraestrutura de <a href="/" className="text-electric font-bold hover:underline">Painel IPTV</a> do Brasil. Tecnologia de ponta, estabilidade absoluta e suporte que entende o seu negócio.
            </p>
            
            <div className="flex flex-col gap-4">
              <a 
                href={whatsappUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm text-muted-foreground group w-fit min-h-[44px]"
                aria-label="Falar conosco no WhatsApp"
              >
                <div className="w-10 h-10 rounded-lg bg-electric/10 text-electric flex items-center justify-center group-hover:bg-electric group-hover:text-white transition-all duration-300 shadow-glow-sm">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">Suporte WhatsApp</span>
                  <span className="font-bold group-hover:text-white transition-colors">+55 (83) 98614-7064</span>
                </div>
              </a>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white mb-6 flex items-center gap-2">
              <span className="w-4 h-[2px] bg-electric rounded-full" />
              Plataforma
            </h2>
            <ul className="flex flex-col gap-3">
              {FOOTER_LINKS.plataforma.map(link => (
                <li key={link.href}>
                  <Link to={link.href as any} className="text-[13px] font-bold text-muted-foreground hover:text-electric transition-all flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white mb-6 flex items-center gap-2">
              <span className="w-4 h-[2px] bg-electric rounded-full" />
              Empresa
            </h2>
            <ul className="flex flex-col gap-3">
              {FOOTER_LINKS.empresa.map(link => (
                <li key={link.href}>
                  <Link to={link.href as any} className="text-[13px] font-bold text-muted-foreground hover:text-electric transition-all flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white mb-6 flex items-center gap-2">
              <span className="w-4 h-[2px] bg-electric rounded-full" />
              Legal
            </h2>
            <ul className="flex flex-col gap-3">
              {FOOTER_LINKS.ajuda.map(link => (
                <li key={link.href}>
                  <Link to={link.href as any} className="text-[13px] font-bold text-muted-foreground hover:text-electric transition-all flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
              © {new Date().getFullYear()} P2 PRO. Todos os direitos reservados.
            </div>
            <div className="flex items-center gap-6">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-electric transition-colors" aria-label="Acessar nosso Instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-electric transition-colors" aria-label="Acessar nosso Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-electric transition-colors" aria-label="Acessar nosso Youtube">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Trusted Badges */}
          <div className="flex items-center gap-4 transition-all duration-500">
            <div className="text-[9px] font-black uppercase tracking-widest mr-2 text-muted-foreground">Pagamento Seguro</div>
            <div className="flex gap-2 text-[10px] font-bold">
              <span className="bg-white/20 text-white px-2 py-1 rounded">PIX</span>
              <span className="bg-white/20 text-white px-2 py-1 rounded">CARTÃO</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}